import React, { useState, useEffect } from 'react';
import { useInsightsData } from '@jwp/ott-hooks-react/src/useInsightsData';
import { useUrlNavigation } from '@jwp/ott-hooks-react/src/useUrlNavigation';
import type { Category, Insight, SidebarArticle } from '@jwp/ott-common/types/insights';
import { useLocation } from 'react-router-dom';

import { Sidebar } from './components/Sidebar/Sidebar';
import { InsightsList } from './components/InsightsList/InsightsList';
import { InsightDetail } from './components/InsightDetail/InsightDetail';
import { LoadingState } from './components/LoadingState/LoadingState';
import styles from './Insights.module.scss';

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

const InsightsComponent: React.FC = () => {
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<Category>>(new Set());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  const location = useLocation();

  const { data, isLoading } = useInsightsData();
  const { insights, sidebarArticles, categories } = data;
  const { updateURL, clearURL, getInsightIdFromURL } = useUrlNavigation();

  const handleInsightClick = (insight: Insight): void => {
    setSelectedInsight(insight);
    updateURL(insight.id);
    setIsMobileSidebarOpen(false);
  };

  useEffect(() => {
    const insightId = getInsightIdFromURL();

    if (!insightId) {
      setSelectedInsight(null);
      return;
    }

    if (insights.length > 0) {
      const insight = insights.find((i) => i.id === insightId) || null;
      setSelectedInsight(insight);
    }
  }, [location.key, insights, getInsightIdFromURL]);

  const handleSidebarClick = (article: SidebarArticle): void => {
    const fullInsight = insights.find((i) => i.id === article.id);
    if (fullInsight) {
      setSelectedInsight(fullInsight);
      updateURL(article.id);
      setIsMobileSidebarOpen(false);
    }
  };

  const handleBackClick = (): void => {
    setSelectedInsight(null);
    clearURL();
  };

  const handleToggleCategory = (category: Category): void => {
    // If sidebar is collapsed, expand it first
    if (isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
    }

    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(category) ? next.delete(category) : next.add(category);
      return next;
    });
  };

  const toggleMobileSidebar = (): void => setIsMobileSidebarOpen((v) => !v);
  const closeMobileSidebar = (): void => setIsMobileSidebarOpen(false);
  const toggleSidebarCollapse = (): void => setIsSidebarCollapsed((v) => !v);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileSidebarOpen) setIsMobileSidebarOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    const insightId = getInsightIdFromURL();
    if (insightId && insights.length > 0) {
      const insight = insights.find((i) => i.id === insightId);
      if (insight) setSelectedInsight(insight);
    }
  }, [insights, getInsightIdFromURL]);

  if (isLoading) return <LoadingState />;

  if (!insights.length) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.pageTitle}>Latest Articles</div>
        <p>No articles available at the moment.</p>
      </div>
    );
  }

  const renderMainContent = (): JSX.Element => {
    if (selectedInsight) {
      return <InsightDetail insight={selectedInsight} onBack={handleBackClick} />;
    }

    return <InsightsList insights={insights} onInsightClick={handleInsightClick} />;
  };

  const renderSidebar = (): JSX.Element => (
    <div
      className={`${styles.sidebar} ${isMobileSidebarOpen ? styles.open : ''} ${isSidebarCollapsed ? styles.collapsed : ''}`}
      aria-modal="true"
      role="dialog"
    >
      <Sidebar
        categories={categories}
        sidebarArticles={sidebarArticles}
        expandedCategories={expandedCategories}
        onToggleCategory={handleToggleCategory}
        onArticleClick={handleSidebarClick}
        isCollapsed={isSidebarCollapsed}
      />
    </div>
  );

  return (
    <>
      <div className={styles.insightsContainer}>
        {renderSidebar()}
        <main className={`${styles.mainContent} ${isSidebarCollapsed ? styles.expandedContent : ''}`}>{renderMainContent()}</main>
      </div>

      {/* Mobile sidebar toggle button */}
      <button
        className={styles.sidebarToggle}
        onClick={toggleMobileSidebar}
        aria-label={isMobileSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        aria-expanded={isMobileSidebarOpen}
        aria-controls="mobile-sidebar"
      >
        {isMobileSidebarOpen ? <CloseIcon /> : <MenuIcon />}
      </button>

      {/* Desktop/tablet sidebar collapse button */}
      <button
        className={styles.sidebarCollapseToggle}
        onClick={toggleSidebarCollapse}
        aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!isSidebarCollapsed}
      >
        {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </button>

      <div className={`${styles.sidebarOverlay} ${isMobileSidebarOpen ? styles.visible : ''}`} onClick={closeMobileSidebar} aria-hidden="true" />
    </>
  );
};

export default InsightsComponent;
